import React, { useEffect, useRef, useState } from 'react';
import { Button, Flex, Input, Modal, App } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import SText from '@/styles/SText';
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from '@/styles/STableLayout';
import { LocationRefProduct } from '@/types/inventoryV2';
import { locProdRefList, locProdRefDels } from '@/apis/inventoryv2';
import { CommonListParams } from '@/types/common';
import { AnyObject } from 'antd/es/_util/type';
import useDataList from '@/hooks/useDataList';
import { getLocationCode } from '../../utils';
import { SConModal } from '@/styles/SConModal';
import { getLocationIdx } from '@/utils/wLocation';
import ListFilterBar from '@/components/ListFilterBar';
import useCommittedFilters from '@/components/ListFilterBar/hooks/useCommittedFilters';

interface LocationRefFilters {
  keyword: string;
}

const INITIAL_LOCATION_REF_FILTERS: LocationRefFilters = {
  keyword: '',
};

const LocationRefProducts: React.FC = () => {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const tableRef = useRef<TableLayoutRef | null>(null);
  // const [selectProductOpen, setSelectProductOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<LocationRefFilters>(INITIAL_LOCATION_REF_FILTERS);
  const { keyword } = draftFilters;

  const [list, listTotal, listLoading, { fetchList, rmItems }] = useDataList<LocationRefProduct, [CommonListParams]>({
    fetchApi: locProdRefList
  });

  const columns: ColumnsType<LocationRefProduct> = [
    {
      title: <SText text={t('warehouse.locationCode')} />,
      dataIndex: 'location',
      key: 'locationCode',
      width: 260,
      render: (location: any) => <SText text={getLocationCode(location)} style={{ width: '100%' }} />
    },
    {
      title: <SText text={t('warehouse.locationIndex')} />,
      dataIndex: 'index',
      key: 'index',
      width: 180,
      render: (_, location) => <SText text={getLocationIdx(location.location)} style={{ width: '100%' }} />
    },
    {
      title: <SText text={t('product.code')} />,
      dataIndex: 'product',
      key: 'productCode',
      width: 150,
      render: (product: any) => <SText text={product?.code || ''} style={{ width: '100%' }} />
    },
    {
      title: <SText text={t('product.name')} />,
      dataIndex: 'product',
      key: 'productName',
      width: 200,
      render: (product: any) => <SText text={product?.name || ''} style={{ width: '100%' }} />
    },
    {
      title: <SText text={t('product.brand1')} />,
      dataIndex: 'product',
      key: 'model',
      width: 150,
      render: (product: any) => <SText text={product?.model || ''} style={{ width: '100%' }} />
    },
    {
      title: <SText text={t('product.uom')} />,
      dataIndex: 'product',
      key: 'uom',
      width: 80,
      render: (product: any) => <SText text={product?.uom || ''} style={{ width: '100%' }} />
    },
    {
      title: t('common.desc'),
      dataIndex: 'desc',
      key: 'desc',
      width: 200,
      render: (text: string) => <SText text={text || ''} />
    },
    {
      title: t('common.opt'),
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(record.id)}
        >
          {t('common.del')}
        </Button>
      )
    },
  ];

  // ✅ 取消请求：记录当前进行中的请求控制器
  const abortControllerRef = useRef<AbortController | null>(null);

  // 组件卸载时取消进行中的请求
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    const nextFilters = replaceFilters(INITIAL_LOCATION_REF_FILTERS);
    fetch(0, nextFilters);
    // 首次加载只执行一次，筛选提交由事件显式触发。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replaceFilters]);

  const fetch = (offset: number, filters: LocationRefFilters) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
    // ✅ 取消上一次还在进行中的请求，避免旧响应覆盖新响应
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    fetchList({ pagination: { offset, take }, body: filters.keyword, controller });
  };

  const handleSearch = () => {
    const nextFilters = commitFilters();
    setSelectedRowKeys([]);
    tableRef?.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_LOCATION_REF_FILTERS);
    setSelectedRowKeys([]);
    tableRef?.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  const handleDelete = async (id: string) => {
    Modal.confirm({
      title: t('common.tip'),
      content: t('warehouse.confirmDeleteItem'),
      onOk: async () => {
        try {
          await locProdRefDels([id]);
          rmItems([id]);
          notification.success({ placement: 'top', message: t('common.deleteSuccess') });
        } catch (err) {
          notification.error({ placement: 'top', message: t('common.deleteFail') });
        }
      }
    });
  };

  const handleDeleteBatch = async () => {
    if (selectedRowKeys.length === 0) {
      notification.warning({ placement: 'top', message: t('common.selectLeastOne') });
      return;
    }
    setDeleteLoading(true);
    try {
      await locProdRefDels(selectedRowKeys as string[]);
      rmItems(selectedRowKeys as string[]);
      setSelectedRowKeys([]);
      notification.success({ placement: 'top', message: t('common.deleteSuccess') });
    } catch (err) {
      notification.error({ placement: 'top', message: t('common.deleteFail') });
    } finally {
      setDeleteLoading(false);
    }
  };


  // const handleSelectLocation = () => {
  //   setSelectProductOpen(true);
  // };

  const headerChildren = (
    <Flex gap={10} align='center' justify='space-between' style={{ width: '100%' }}>
      <Flex gap={10} align='center'>
        {/* <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          onClick={handleSelectLocation}
        >
          {t('warehouse.addLocationRef')}
        </Button> */}
        <SConModal
          title={t('common.tip')}
          content={t('warehouse.confirmBatchDelete')}
          onOk={handleDeleteBatch}
          disable={selectedRowKeys.length === 0 || deleteLoading}
          okText={t('common.batchDel')}
        >
          <Button
            danger
            disabled={selectedRowKeys.length === 0 || deleteLoading}
            loading={deleteLoading}
          >
            {t('common.batchDel')}
          </Button>
        </SConModal>
      </Flex>
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
          disabled={listLoading}
        >
          {({ submit }) => (
            <Input
              style={{ width: 180 }}
              placeholder={t('warehouse.searchMaterialPlaceholder')}
              value={keyword}
              onChange={event => updateDraftFilters({ keyword: event.target.value })}
              onPressEnter={submit}
            />
          )}
        </ListFilterBar>
      </div>
    </Flex>
  );

  return (
    <Flex vertical style={{ width: "100%", height: '100%' }}>
      <STableLayout
        rowKey={"id"}
        headerChildren={headerChildren}
        tableLayoutRef={tableRef}
        total={listTotal}
        handleChange={offset => fetch(offset, appliedFilters)}
        columns={columns as ColumnsType<AnyObject>}
        dataSource={list}
        loading={listLoading}
        scrollX={1200}
        rowSelection={{
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys),
        }}
      />
    </Flex>
  );
};

export default LocationRefProducts;
